"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Tabs = exports.Tab = exports.TabPanel = void 0;
var PropTypes = require("prop-types");
var React = require("react");
var react_1 = require("react");
var smoothscroll = require("smoothscroll-polyfill");
var Icon_1 = require("../Icon");
var TabsUtils_1 = require("./TabsUtils");
// polyfill for smooth scroll
smoothscroll.polyfill();
var TabPanel = function (props) {
    var children = props.children, className = props.className, controlId = props.controlId, isActive = props.isActive;
    var classNames = className ? className + " fade tab-pane" : 'fade tab-pane';
    return (React.createElement("div", { role: "tabpanel", id: controlId + "-pane", className: isActive ? classNames + " active show" : classNames, "aria-hidden": !isActive, "aria-labelledby": controlId }, isActive && children));
};
exports.TabPanel = TabPanel;
var Tab = function (props) {
    var children = props.children, controlId = props.controlId, isActive = props.isActive, isFocused = props.isFocused, onClick = props.onClick, 
    /* tslint:disable:no-empty */
    _a = props.onKeyDown, 
    /* tslint:disable:no-empty */
    onKeyDown = _a === void 0 ? function () { } : _a;
    var domRef = (React.useRef());
    // set the focused element
    (0, react_1.useEffect)(function () {
        if (isFocused && domRef.current) {
            domRef.current.focus();
        }
    }, [isFocused, domRef]);
    // scroll to active tab if out of view
    (0, react_1.useEffect)(function () {
        if (isActive && domRef.current) {
            var domParent = domRef.current.offsetParent;
            /* istanbul ignore if */
            if (domParent !== null) {
                // required to pass teamcity test cases only
                if (typeof domParent.getBoundingClientRect === 'function') {
                    var domRefRight = domRef.current.getBoundingClientRect().right;
                    var domParentRight = domParent.getBoundingClientRect().right;
                    // required for refresh on a page where the user has scrolled prior
                    domParent.scrollLeft = 0;
                    if (domRefRight > domParentRight || domRefRight > 0) {
                        domParent.scrollLeft = domRefRight;
                    }
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return (React.createElement("button", { role: "tab", id: controlId, ref: domRef, onClick: onClick, onKeyDown: onKeyDown, className: "nav-link " + (isActive ? 'active' : ''), "aria-controls": controlId + "-pane", "aria-selected": isActive }, children));
};
exports.Tab = Tab;
var Tabs = function (props) {
    var _a;
    var ariaLabel = props.ariaLabel, tabs = props.tabs, tabPanels = props.tabPanels;
    var _b = (0, react_1.useState)(0), activeTabIndex = _b[0], setActiveTabIndex = _b[1];
    var _c = (0, react_1.useState)(undefined), focusedTabIndex = _c[0], setFocusedTabIndex = _c[1];
    var _d = (0, react_1.useState)(-1), firstLoadActiveIndex = _d[0], setFirstLoadActiveIndex = _d[1];
    var _e = (0, react_1.useState)(-1), firstLoadTabIndex = _e[0], setFirstLoadTabIndex = _e[1];
    var onKeyDown = function (e, index, tabsLength) {
        if (e) {
            /* tslint:disable:one-variable-per-declaration */
            var key = e.which
                ? e.which.toString() : e.keycode.toString(), currentIndex = index;
            // on ArrowLeft or ArrowRight
            if (key.match(/37|39/)) {
                e.preventDefault();
                /* tslint:disable:one-variable-per-declaration */
                var direction = key.match(/39/)
                    ? 1 : -1, newIndex = (currentIndex + tabsLength + direction) % tabsLength;
                setFocusedTabIndex(newIndex);
                // on enter: 13, space: 32
            }
            else if (key.match(/13|32/)) {
                e.preventDefault();
                setActiveTabIndex(index);
            }
        }
    };
    // next/prev buttons logic
    var _f = (0, react_1.useState)(false), prevShow = _f[0], setPrevShow = _f[1];
    var _g = (0, react_1.useState)(false), nextShow = _g[0], setNextShow = _g[1];
    var navTabsWrapperRef = React.useRef(null);
    var navTabsRef = React.useRef(null);
    var nextBtnRef = React.useRef(null);
    var prevBtnRef = React.useRef(null);
    var navTabsChildren = (_a = navTabsRef.current) === null || _a === void 0 ? void 0 : _a.children;
    var getButtonsWidthArray = function () {
        // creat array of button tab width
        if (navTabsChildren === null || navTabsChildren === void 0 ? void 0 : navTabsChildren.length) {
            return Array.prototype.map.call(navTabsChildren, function (btn) { return btn.offsetWidth; });
        }
        return [];
    };
    var onClickNext = function (e) {
        var _a, _b;
        var newScrollLeft = (0, TabsUtils_1.getNewScrollLeft)({
            buttonsWidthArray: getButtonsWidthArray(),
            direction: 'next',
            gapBetweenBtns: window.innerWidth > 768 ? 2 : 0,
            wrapperScrollLeft: ((_a = navTabsWrapperRef.current) === null || _a === void 0 ? void 0 : _a.scrollLeft) || 0,
            wrapperWidth: ((_b = navTabsWrapperRef.current) === null || _b === void 0 ? void 0 : _b.offsetWidth) || 0,
        });
        if (navTabsWrapperRef.current) {
            navTabsWrapperRef.current.scroll({
                behavior: 'smooth',
                left: newScrollLeft,
            });
        }
    };
    var onClickPrev = function (e) {
        var _a, _b;
        var newScrollLeft = (0, TabsUtils_1.getNewScrollLeft)({
            buttonsWidthArray: getButtonsWidthArray(),
            direction: 'prev',
            gapBetweenBtns: window.innerWidth > 768 ? 2 : 0,
            wrapperScrollLeft: ((_a = navTabsWrapperRef.current) === null || _a === void 0 ? void 0 : _a.scrollLeft) || 0,
            wrapperWidth: ((_b = navTabsWrapperRef.current) === null || _b === void 0 ? void 0 : _b.offsetWidth) || 0,
        });
        if (navTabsWrapperRef.current) {
            navTabsWrapperRef.current.scroll({
                behavior: 'smooth',
                left: newScrollLeft,
            });
        }
    };
    (0, react_1.useLayoutEffect)(function () {
        var tabsRef = navTabsWrapperRef.current;
        var onScrollFn = function () {
            var _a;
            return (0, TabsUtils_1.onScroll)({
                setNextShow: setNextShow,
                setPrevShow: setPrevShow,
                tabsOffsetWidth: ((_a = navTabsRef.current) === null || _a === void 0 ? void 0 : _a.offsetWidth) || 0,
                wrapperOffsetWidth: (tabsRef === null || tabsRef === void 0 ? void 0 : tabsRef.offsetWidth) || 0,
                wrapperScrollLeft: (tabsRef === null || tabsRef === void 0 ? void 0 : tabsRef.scrollLeft) || 0,
            });
        };
        onScrollFn();
        tabsRef === null || tabsRef === void 0 ? void 0 : tabsRef.addEventListener('scroll', onScrollFn);
        window.addEventListener('resize', onScrollFn);
        return function () {
            tabsRef === null || tabsRef === void 0 ? void 0 : tabsRef.removeEventListener('scroll', onScrollFn);
            window.removeEventListener('resize', onScrollFn);
        };
    }, []);
    var attributes = {
        'aria-label': ariaLabel ? ariaLabel : 'Navigation',
        className: "nav nav-tabs",
        role: 'tablist',
    };
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { className: "nav-tabs--container" },
            prevShow &&
                React.createElement("button", { "aria-hidden": true, tabIndex: -1, onClick: onClickPrev, ref: prevBtnRef, className: "prev" },
                    React.createElement(Icon_1.Icon, { className: "icon-rotated", symbol: "controlChevronForward24", type: "svginline" })),
            React.createElement("div", { ref: navTabsWrapperRef, className: "nav-tabs--wrapper" },
                React.createElement("div", __assign({}, attributes, { ref: navTabsRef }), tabs === null || tabs === void 0 ? void 0 : tabs.map(function (child, index) {
                    var onClick = function (e) {
                        setActiveTabIndex(index);
                        if (child.props.onClick) {
                            child.props.onClick(e);
                        }
                    };
                    var onKeyDownHandler = function (e, tabIndex, tabsLength) {
                        onKeyDown(e, tabIndex, tabsLength);
                        if (child.props.onKeyDown) {
                            child.props.onKeyDown(e);
                        }
                    };
                    if (child.type === exports.Tab) {
                        if (child.props.isActive && index !== activeTabIndex && firstLoadActiveIndex === -1) {
                            setActiveTabIndex(index);
                            setFirstLoadActiveIndex(index);
                        }
                        if (child.props.isFocused && index !== activeTabIndex && firstLoadTabIndex === -1) {
                            setFocusedTabIndex(index);
                            setFirstLoadTabIndex(index);
                        }
                        return (React.cloneElement(child, {
                            index: index,
                            isActive: index === activeTabIndex,
                            isFocused: index === focusedTabIndex,
                            key: index,
                            onClick: onClick,
                            onKeyDown: function (e) { return onKeyDownHandler(e, index, tabs.length); },
                            tabsLength: tabs.length,
                        }));
                    }
                    throw new Error("You need to add <Tab> as a child, not a " + child.type);
                }))),
            nextShow &&
                React.createElement("button", { "aria-hidden": true, tabIndex: -1, ref: nextBtnRef, onClick: onClickNext, className: "next" },
                    React.createElement(Icon_1.Icon, { symbol: "controlChevronForward24", type: "svginline" }))), tabPanels === null || tabPanels === void 0 ? void 0 :
        tabPanels.map(function (child, index) {
            /* istanbul ignore next */
            if (child.type === exports.TabPanel) {
                return (React.cloneElement(child, {
                    isActive: index === activeTabIndex,
                    key: index,
                }));
            }
            /* istanbul ignore next */
            throw new Error("You need to add <TabPanel> as a child, not a " + child.type);
        })));
};
exports.Tabs = Tabs;
/* tslint:disable:no-string-literal */
exports.Tab['propTypes'] = {
    children: PropTypes.any,
    controlId: PropTypes.string.isRequired,
    index: PropTypes.number,
    isActive: PropTypes.bool,
    isFocused: PropTypes.bool,
    onClick: PropTypes.func,
    onKeyDown: PropTypes.func,
    tabsLength: PropTypes.number,
};
exports.TabPanel['propTypes'] = {
    children: PropTypes.any,
    className: PropTypes.string,
    controlId: PropTypes.string.isRequired,
    isActive: PropTypes.bool,
};
/* tslint:enable:no-string-literal */
